import * as React from "react";
import { useTheme } from "@mui/material/styles";
import { Box, Paper, Stack, Typography } from "@mui/material";

import { PERM_UPPER_RIGHT, PERM_UPPER_LEFT, PERM_LOWER_RIGHT, PERM_LOWER_LEFT } from "../../../../config/constants/Constants";
import { compareExaminations, getEffectiveTooth } from "../../../../helper-functions/shared/chartUtils";
import { SECTIONS, getSectionPolygon, getSiteKey, getToothSize, getOutlinePath, LAYOUT } from "../../../../helper-functions/shared/toothChartConstants";

/**
 * Section header for comparison categories
 */
const SectionHeader = ({ title, color, count }) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1.5, mb: 0.5 }}>
        <Box sx={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: color, flexShrink: 0 }} />
        <Typography component="span" sx={{ fontWeight: 700, fontSize: "11px" }}>
            {title} ({count})
        </Typography>
    </Box>
);

// ─── Chart colors ───
const COLORS = {
    improved: "#4caf50",
    deteriorated: "#f44336",
    remaining: "#ff9800"
};

/**
 * ExaminationCompare Component
 * Displays comparison between current and previous periodontal examination data.
 * Shows changes in probing pocket depths and bleeding on probing.
 */
export default function ExaminationCompare({ teethData, previousTeethData, patientInfo = {} }) {
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";
    const chartId = React.useId();

    if (!teethData || !previousTeethData) return null;

    const comparison = compareExaminations(teethData, previousTeethData);

    const lineStyle = { lineHeight: 1.8, color: isDark ? "#ccc" : "#333", fontSize: "11px" };
    const siteListStyle = { ...lineStyle, pl: 2, wordBreak: "break-word" };

    const formatPDSites = (sites) => sites.map((s) => `${s.site} (${s.previous}mm → ${s.current}mm)`).join(", ");

    const hasPDChanges = comparison.probingDepth.improved.length > 0 || comparison.probingDepth.deteriorated.length > 0;
    const hasBOPChanges = comparison.bleeding.improved.length > 0 || comparison.bleeding.deteriorated.length > 0 || comparison.bleeding.remaining.length > 0;
    const hasLostTeeth = comparison.teeth?.lost?.length > 0;

    // ─── Shared tooth chart helpers ───
    const upperTeeth = [...PERM_UPPER_RIGHT, ...PERM_UPPER_LEFT];
    const lowerTeeth = [...PERM_LOWER_RIGHT, ...PERM_LOWER_LEFT];

    // ─── Layout ───
    const { svgWidth, maxRy, rowHeight } = LAYOUT;
    const legendH = 16;
    const labelArea = 14;
    const svgHeight = legendH + labelArea + rowHeight + labelArea + rowHeight + labelArea;
    const legendY = legendH / 2;
    const topLabelY = legendH + labelArea / 2;
    const upperRowY = legendH + labelArea + maxRy + 2;
    const midLabelY = legendH + labelArea + rowHeight + labelArea / 2;
    const lowerRowY = legendH + labelArea + rowHeight + labelArea + maxRy + 2;
    const bottomLabelY = legendH + labelArea + rowHeight + labelArea + rowHeight + labelArea / 2;

    // ─── Colors ───
    const bgColor = isDark ? "#3a3a3a" : "#fafafa";
    const lineColor = isDark ? "#777" : "#999";
    const missingLineColor = isDark ? "#666" : "#b0b0b0";
    const labelColor = isDark ? "#888" : "#999";
    const ghostFill = isDark ? "rgba(100,100,100,0.12)" : "rgba(200,200,200,0.25)";
    const toothNumColor = isDark ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.4)";
    const ghostNumColor = isDark ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.15)";

    /**
     * Render a single tooth with colored wedge sections based on a color-getter function.
     * @param {number} permanentToothNum - FDI tooth number
     * @param {number} idx - position index in arch
     * @param {number} rowY - Y center of this row
     * @param {string} prefix - unique prefix for clip IDs
     * @param {Function} getWedgeColor - (toothNum, siteKey) => fill color or null
     */
    const renderComparisonTooth = (permanentToothNum, idx, rowY, prefix, getWedgeColor) => {
        const etn = getEffectiveTooth(teethData, permanentToothNum);
        const tooth = teethData[etn];
        const isMissing = !tooth || tooth.missing;
        const { rx, ry } = getToothSize(etn);
        const isImplant = !isMissing && tooth?.implant;
        const r = Math.min(rx, ry);
        const outline = isImplant ? `M ${-r},0 A ${r},${r} 0 1,0 ${r},0 A ${r},${r} 0 1,0 ${-r},0 Z` : getOutlinePath(etn);
        const x = LAYOUT.getToothX(idx);
        const clipId = `${chartId}-${prefix}-${permanentToothNum}`;
        const sections = getSectionPolygon(rx, ry);
        const e = 1.5;

        return (
            <g key={permanentToothNum} transform={`translate(${x}, ${rowY})`}>
                <defs>
                    <clipPath id={clipId}>
                        <path d={outline} />
                    </clipPath>
                </defs>

                <path d={outline} fill={isMissing ? ghostFill : bgColor} stroke="none" />

                <g clipPath={`url(#${clipId})`}>
                    {!isMissing && (
                        <>
                            {SECTIONS.map((section) => {
                                const siteKey = getSiteKey(etn, section);
                                const color = getWedgeColor(permanentToothNum, siteKey);
                                if (!color) return null;
                                return <polygon key={section} points={sections[section]} fill={color} fillOpacity={0.7} />;
                            })}
                            <line x1={-rx * e} y1={-ry * e} x2={rx * e} y2={ry * e} stroke={lineColor} strokeWidth={0.7} />
                            <line x1={rx * e} y1={-ry * e} x2={-rx * e} y2={ry * e} stroke={lineColor} strokeWidth={0.7} />
                            <line x1={-rx * e} y1={0} x2={rx * e} y2={0} stroke={lineColor} strokeWidth={0.7} />
                        </>
                    )}
                </g>

                <text x={0} y={3} textAnchor="middle" dominantBaseline="central" fontSize={9} fontWeight="bold" pointerEvents="none" fill={isMissing ? ghostNumColor : toothNumColor}>
                    {etn}
                </text>

                {isImplant ? (
                    <>
                        <circle cx={0} cy={0} r={r} fill="none" stroke={lineColor} strokeWidth={1.2} />
                        <circle cx={0} cy={0} r={r + 3} fill="none" stroke={lineColor} strokeWidth={1.0} />
                    </>
                ) : (
                    <path d={outline} fill="none" stroke={isMissing ? missingLineColor : lineColor} strokeWidth={isMissing ? 1.0 : 1.2} strokeDasharray={isMissing ? "3,2" : "none"} />
                )}
            </g>
        );
    };

    /**
     * Render a full tooth chart (upper + lower arch) with a title, legend, and summary.
     */
    const renderComparisonChart = (title, prefix, legendItems, getWedgeColor, summaryItems) => (
        <Paper elevation={0} sx={{ p: { xs: 1, sm: 2 }, width: "100%" }}>
            <Typography variant="subtitle1" fontWeight="bold" color="text.primary" mb={1}>
                {title}
            </Typography>

            <Box sx={{ display: "flex", alignItems: "center" }}>
                <Box sx={{ flex: 1, overflow: "auto", "& svg": { maxWidth: "100%", height: "auto" } }}>
                    <svg viewBox={`0 0 ${svgWidth} ${svgHeight}`} width="100%" preserveAspectRatio="xMidYMid meet" style={{ fontFamily: "inherit", display: "block", minWidth: 400 }}>
                        {/* Legend */}
                        {legendItems.map((item, i) => {
                            const lw = svgWidth / legendItems.length;
                            return (
                                <g key={item.label} transform={`translate(${lw * i + lw / 2 - 30}, ${legendY})`}>
                                    <rect x={0} y={-5} width={10} height={10} rx={1} fill={item.color} fillOpacity={0.7} />
                                    <text x={14} y={0} fontSize={8} fill={isDark ? "#ccc" : "#555"} dominantBaseline="central">
                                        {item.label}
                                    </text>
                                </g>
                            );
                        })}

                        {/* Orientation labels */}
                        <text x={svgWidth / 2} y={topLabelY} textAnchor="middle" dominantBaseline="central" fontSize={10} fill={labelColor} fontStyle="italic">
                            Buccal
                        </text>
                        <text x={svgWidth / 2} y={midLabelY} textAnchor="middle" dominantBaseline="central" fontSize={10} fill={labelColor} fontStyle="italic">
                            Lingual
                        </text>
                        <text x={svgWidth / 2} y={bottomLabelY} textAnchor="middle" dominantBaseline="central" fontSize={10} fill={labelColor} fontStyle="italic">
                            Buccal
                        </text>

                        {upperTeeth.map((tooth, idx) => renderComparisonTooth(tooth, idx, upperRowY, prefix, getWedgeColor))}
                        {lowerTeeth.map((tooth, idx) => renderComparisonTooth(tooth, idx, lowerRowY, prefix, getWedgeColor))}
                    </svg>
                </Box>

                <Stack sx={{ ml: { xs: 1, sm: 2 }, minWidth: { xs: 40, sm: 60 }, textAlign: "right" }}>
                    {summaryItems.map((item) => (
                        <Box key={item.label}>
                            <Typography variant="h6" fontWeight="bold" sx={{ color: item.color, whiteSpace: "nowrap", lineHeight: 1.3 }}>
                                {item.count}
                            </Typography>
                            <Typography variant="caption" sx={{ color: "text.secondary", whiteSpace: "nowrap", fontSize: "0.6rem" }}>
                                {item.label}
                            </Typography>
                        </Box>
                    ))}
                </Stack>
            </Box>
        </Paper>
    );

    // ─── PD chart: color getter ───
    const getPDWedgeColor = (permanentToothNum, siteKey) => {
        const status = comparison.siteMap.get(`${permanentToothNum}-${siteKey}`);
        if (!status) return null;
        if (status.startsWith("pd-improved")) return COLORS.improved;
        if (status.startsWith("pd-deteriorated")) return COLORS.deteriorated;
        return null;
    };

    // ─── BOP chart: color getter ───
    const getBOPWedgeColor = (permanentToothNum, siteKey) => {
        const status = comparison.siteMap.get(`${permanentToothNum}-${siteKey}`);
        if (!status) return null;
        if (status.includes("bop-improved")) return COLORS.improved;
        if (status.includes("bop-deteriorated")) return COLORS.deteriorated;
        if (status.includes("bop-remaining")) return COLORS.remaining;
        return null;
    };

    return (
        <Paper
            elevation={0}
            sx={{
                width: "100%",
                overflow: "auto",
                maxWidth: "100%",
                margin: "0 auto",
                backgroundColor: isDark ? "#1e1e1e" : "#ffffff"
            }}>
            {/* Header */}
            <Box
                sx={{
                    py: 1.5,
                    px: 2,
                    textAlign: "center"
                }}>
                <Typography variant="h6" fontWeight="bold">
                    Examination Comparison
                </Typography>
                {patientInfo.name && (
                    <Typography variant="body2" color="text.secondary">
                        Patient: {patientInfo.name} | Current: {patientInfo.currentDate} | Previous: {patientInfo.previousDate}
                    </Typography>
                )}
            </Box>

            {/* Probing Depth Changes (text) */}
            <Box
                sx={{
                    backgroundColor: isDark ? "#1e3a5f" : "#e3f2fd",
                    border: `1px solid ${isDark ? "#4995e0" : "#1976d2"}`,
                    p: 2,
                    mx: 2,
                    mb: 1,
                    mt: 1,
                    fontSize: "11px"
                }}>
                <Typography sx={{ fontWeight: 700, fontSize: "12px", color: isDark ? "#fff" : "#000" }}>Changes in Probing Pocket Depth</Typography>
                <Box sx={{ ...lineStyle, fontStyle: "italic" }}>A site is at risk at ≥5mm, or at 4mm with bleeding on probing.</Box>

                {!hasPDChanges && <Box sx={{ ...lineStyle, mt: 1, fontStyle: "italic" }}>No significant probing depth changes detected.</Box>}

                {comparison.probingDepth.improved.length > 0 && (
                    <>
                        <SectionHeader title="Improved (no longer at risk)" color="#4caf50" count={comparison.probingDepth.improved.length} />
                        <Box sx={siteListStyle}>{formatPDSites(comparison.probingDepth.improved)}</Box>
                    </>
                )}

                {comparison.probingDepth.deteriorated.length > 0 && (
                    <>
                        <SectionHeader
                            title="Deteriorated (newly at risk, or still at risk and deeper)"
                            color="#f44336"
                            count={comparison.probingDepth.deteriorated.length}
                        />
                        <Box sx={siteListStyle}>{formatPDSites(comparison.probingDepth.deteriorated)}</Box>
                    </>
                )}

                {hasLostTeeth && (
                    <>
                        <SectionHeader title="Teeth lost since previous examination" color="#f44336" count={comparison.teeth.lost.length} />
                        <Box sx={siteListStyle}>{comparison.teeth.lost.join(", ")}</Box>
                    </>
                )}
            </Box>

            {/* Bleeding on Probing Changes (text) */}
            <Box
                sx={{
                    backgroundColor: isDark ? "#3a1e1e" : "#fce4ec",
                    border: `1px solid ${isDark ? "#e05549" : "#d32f2f"}`,
                    p: 2,
                    mx: 2,
                    mb: 2,
                    fontSize: "11px"
                }}>
                <Typography sx={{ fontWeight: 700, fontSize: "12px", color: isDark ? "#fff" : "#000" }}>Changes in Bleeding on Probing</Typography>

                {!hasBOPChanges && <Box sx={{ ...lineStyle, mt: 1, fontStyle: "italic" }}>No bleeding on probing changes detected.</Box>}

                {comparison.bleeding.improved.length > 0 && (
                    <>
                        <SectionHeader title="Improved (no longer bleeding)" color="#4caf50" count={comparison.bleeding.improved.length} />
                        <Box sx={siteListStyle}>{comparison.bleeding.improved.join(", ")}</Box>
                    </>
                )}

                {comparison.bleeding.deteriorated.length > 0 && (
                    <>
                        <SectionHeader title="Deteriorated (new bleeding)" color="#f44336" count={comparison.bleeding.deteriorated.length} />
                        <Box sx={siteListStyle}>{comparison.bleeding.deteriorated.join(", ")}</Box>
                    </>
                )}

                {comparison.bleeding.remaining.length > 0 && (
                    <>
                        <SectionHeader title="Remaining bleeding (both visits)" color="#ff9800" count={comparison.bleeding.remaining.length} />
                        <Box sx={siteListStyle}>{comparison.bleeding.remaining.join(", ")}</Box>
                    </>
                )}
            </Box>

            {/* Visual Comparison Charts */}
            <Stack spacing={2} sx={{ mx: 2, mb: 2 }}>
                {/* Probing Depth Changes Chart */}
                {hasPDChanges &&
                    renderComparisonChart(
                        "Probing Depth Changes",
                        "pd-cmp",
                        [
                            { label: "Improved", color: COLORS.improved },
                            { label: "Deteriorated", color: COLORS.deteriorated }
                        ],
                        getPDWedgeColor,
                        [
                            { label: "improved", color: COLORS.improved, count: comparison.probingDepth.improved.length },
                            { label: "deteriorated", color: COLORS.deteriorated, count: comparison.probingDepth.deteriorated.length }
                        ]
                    )}

                {/* Bleeding on Probing Changes Chart */}
                {hasBOPChanges &&
                    renderComparisonChart(
                        "Bleeding on Probing Changes",
                        "bop-cmp",
                        [
                            { label: "Resolved", color: COLORS.improved },
                            { label: "New", color: COLORS.deteriorated },
                            { label: "Persistent", color: COLORS.remaining }
                        ],
                        getBOPWedgeColor,
                        [
                            { label: "resolved", color: COLORS.improved, count: comparison.bleeding.improved.length },
                            { label: "new", color: COLORS.deteriorated, count: comparison.bleeding.deteriorated.length },
                            { label: "persistent", color: COLORS.remaining, count: comparison.bleeding.remaining.length }
                        ]
                    )}
            </Stack>
        </Paper>
    );
}
